import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type SessionRecord } from "@/shared/domain/sessions";

import { sessionQueryKeys } from "./sessionQueryKeys";

/** How many sessions are marked complete at the same time. */
const MAX_CONCURRENT_COMPLETIONS = 4;

export interface UseCompleteSessionsVariables {
  sessionIds: string[];
}

export interface CompleteSessionsResult {
  /** The complete record of every session that was marked complete. */
  completed: SessionRecord[];
  /** Every session the server refused or failed to mark complete. */
  failed: { sessionId: string; error: Error }[];
}

/**
 * Mark sessions complete, a few requests at a time. One session failing does
 * not stop the others, so the mutation itself never fails: it resolves with
 * which sessions were and were not marked complete.
 *
 * Each completed session's detail cache takes its complete record and its
 * ingestion status is refetched; the session lists and their ingestion
 * summaries are refetched once.
 */
export function useCompleteSessions() {
  const { sessionService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    CompleteSessionsResult,
    Error,
    UseCompleteSessionsVariables
  >({
    mutationFn: async ({ sessionIds }) => {
      const result: CompleteSessionsResult = { completed: [], failed: [] };
      const queue = [...sessionIds];
      async function work() {
        for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
          try {
            result.completed.push(await sessionService.completeSession(id));
          } catch (error) {
            result.failed.push({
              sessionId: id,
              error: error instanceof Error ? error : new Error(String(error)),
            });
          }
        }
      }
      await Promise.all(
        Array.from(
          { length: Math.min(MAX_CONCURRENT_COMPLETIONS, sessionIds.length) },
          work,
        ),
      );
      return result;
    },
    onSuccess: ({ completed }) => {
      for (const record of completed) {
        queryClient.setQueryData(
          sessionQueryKeys.detail(record.session_id),
          record,
        );
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.ingestion(record.session_id),
        });
      }
      if (completed.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.lists(),
        });
        void queryClient.invalidateQueries({
          queryKey: sessionQueryKeys.allIngestionSummaries(),
        });
      }
    },
  });
}
